// src/Vendedor/components/LeadsHeader.jsx
import { Box, Typography, Paper, ToggleButtonGroup, ToggleButton, Tooltip, TextField, InputAdornment } from "@mui/material";
import ArrowDownwardOutlinedIcon from '@mui/icons-material/ArrowDownwardOutlined';
import ArrowUpwardOutlinedIcon from '@mui/icons-material/ArrowUpwardOutlined';
import AccessTimeOutlinedIcon from '@mui/icons-material/AccessTimeOutlined';
import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';
import { EXCLUSIVE_FILTERS } from "../containers/Principal";

const colors = {
  ink: "#06185f",
  muted: "#4c5d86",
  blue: "#0059f6",
  line: "#d9e3f4",
};

const stateColors = {
  ACTIVO: { border: "#0059f6", bg: "rgba(0, 89, 246, 0.08)", text: "#06185f" },
  VENTA: { border: "#96c87c", bg: "rgba(150, 200, 124, 0.14)", text: "#2f8f3a" },
  BAJA: { border: "#fd8d82", bg: "rgba(253, 141, 130, 0.14)", text: "#e34d43" },
  FUTURO: { border: "#b1b5c2", bg: "rgba(177, 181, 194, 0.16)", text: "#7d8495" },
};

// Versión compacta de StatPaper
function StatPaperCompact({ label, value, selected, onClick }) {
  return (
    <Paper
      onClick={onClick}
      role="button"
      elevation={0}
      sx={{
        minWidth: 60,
        height: 48,
        px: 1.5,
        textAlign: "center",
        borderRadius: "8px",
        cursor: "pointer",
        userSelect: "none",
        transition: "border-color 140ms ease, box-shadow 140ms ease, background-color 140ms ease",
        bgcolor: selected ? "rgba(0,89,246,0.06)" : "rgba(255,255,255,0.94)",
        border: "1px solid",
        borderColor: selected ? colors.blue : colors.line,
        boxShadow: selected ? "0 8px 18px rgba(0, 89, 246, 0.10)" : "0 8px 18px rgba(19, 39, 84, 0.06)",
        "&:hover": {
          borderColor: "rgba(0,89,246,0.45)",
        },
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      <Typography sx={{ color: colors.muted, fontSize: 11, fontWeight: 800, lineHeight: 1.1 }}>
        {label}
      </Typography>
      <Typography sx={{ color: colors.ink, fontSize: 18, fontWeight: 900, lineHeight: 1.25 }}>
        {value}
      </Typography>
    </Paper>
  );
}

// Versión compacta de TogglePaper
function TogglePaperCompact({ label, value, active, onClick, status = "ACTIVO" }) {
  const statusColor = stateColors[status] || stateColors.ACTIVO;
  return (
    <Paper
      onClick={onClick}
      role="button"
      elevation={0}
      sx={{
        minWidth: 60,
        height: 48,
        px: 1.5,
        textAlign: "center",
        borderRadius: "8px",
        cursor: "pointer",
        userSelect: "none",
        transition: "border-color 140ms ease, box-shadow 140ms ease, background-color 140ms ease",
        bgcolor: active ? statusColor.bg : "rgba(255,255,255,0.94)",
        border: "1px solid",
        borderColor: active ? statusColor.border : colors.line,
        boxShadow: active ? "0 8px 18px rgba(19, 39, 84, 0.10)" : "0 8px 18px rgba(19, 39, 84, 0.06)",
        "&:hover": {
          borderColor: statusColor.border,
          bgcolor: statusColor.bg,
        },
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      <Typography sx={{ color: active ? statusColor.text : colors.muted, fontSize: 11, fontWeight: 800, lineHeight: 1.1 }}>
        {label}
      </Typography>
      <Typography sx={{ color: active ? statusColor.text : colors.ink, fontSize: 18, fontWeight: 900, lineHeight: 1.25 }}>
        {value}
      </Typography>
    </Paper>
  );
}

function OrderIcon({ dir = 'desc' }) {
  const ArrowIcon = dir === 'desc' ? ArrowDownwardOutlinedIcon : ArrowUpwardOutlinedIcon;
  return (
    <Box sx={{ position: 'relative', width: 20, height: 20, lineHeight: 0, color: 'inherit' }}>
      <ArrowIcon sx={{ position: 'absolute', left: 0, top: 0, fontSize: 18, color: 'inherit' }} />
      <AccessTimeOutlinedIcon sx={{ position: 'absolute', right: -2, bottom: -2, fontSize: 12, opacity: 0.9, color: 'inherit' }} />
    </Box>
  );
}

export default function LeadsHeader({
  total = 0,
  nuevos = 0,
  atrasados = 0,
  exclusiveFilter = EXCLUSIVE_FILTERS.TOTAL,
  onChangeExclusiveFilter = () => {},
  selectedStates = ["ACTIVO"],
  onToggleState = () => {},
  stateCounts = { ACTIVO: 0, VENTA: 0, BAJA: 0, FUTURO: 0 },
  sortOrder = 'desc',
  onChangeSort = () => {},
  searchTerm = '',
  onSearchChange = () => {},
}) {
  return (
    <Box 
      sx={{ 
        width: "100%", 
        boxSizing: "border-box",
        backgroundColor: "rgba(255,255,255,0.72)",
        border: `1px solid ${colors.line}`,
        borderRadius: "10px",
        py: 1.4,
        pl: 1.4,
        pr: 1,
        display: "flex", 
        alignItems: "center",
        gap: 2.2,
        flexWrap: { xs: "wrap", lg: "nowrap" },
        boxShadow: "0 14px 34px rgba(19, 39, 84, 0.08)",
      }}
    >
      {/* ===== Sección LEADS ===== */}
      <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
        <Typography sx={{ color: colors.ink, fontSize: 16, fontWeight: 900, whiteSpace: "nowrap" }}>
          LEADS
        </Typography>
        <Box sx={{ display: "flex", gap: 0.75 }}>
          <StatPaperCompact label="Totales" value={total} selected={exclusiveFilter === EXCLUSIVE_FILTERS.TOTAL} onClick={() => onChangeExclusiveFilter(EXCLUSIVE_FILTERS.TOTAL)} />
          <StatPaperCompact label="Nuevos" value={nuevos} selected={exclusiveFilter === EXCLUSIVE_FILTERS.NUEVOS} onClick={() => onChangeExclusiveFilter(EXCLUSIVE_FILTERS.NUEVOS)} />
          <StatPaperCompact label="Atrasados" value={atrasados} selected={exclusiveFilter === EXCLUSIVE_FILTERS.ATRASADOS} onClick={() => onChangeExclusiveFilter(EXCLUSIVE_FILTERS.ATRASADOS)} />
        </Box>
      </Box>

      {/* ===== Búsqueda (centro) ===== */}
      <Box sx={{ flex: { xs: "1 1 100%", lg: "0 1 200px" }, order: { xs: 3, lg: 0 } }}>
        <TextField
          size="small"
          placeholder="Buscar cliente..."
          value={searchTerm}
          onChange={(e) => onSearchChange(e.target.value)}
          fullWidth
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ color: 'text.secondary', fontSize: 18 }} />
              </InputAdornment>
            ),
            endAdornment: searchTerm ? (
              <InputAdornment position="end">
                <ClearIcon 
                  sx={{ color: 'text.secondary', fontSize: 16, cursor: 'pointer', '&:hover': { color: 'text.primary' } }} 
                  onClick={() => onSearchChange('')}
                />
              </InputAdornment>
            ) : null,
            sx: {
              borderRadius: 2,
              backgroundColor: 'white',
              height: 36,
              color: colors.ink,
              fontSize: 13,
              fontWeight: 600,
              boxShadow: "0 8px 18px rgba(19, 39, 84, 0.06)",
              '& .MuiOutlinedInput-notchedOutline': {
                borderColor: colors.line,
              },
              '&:hover .MuiOutlinedInput-notchedOutline': {
                borderColor: 'rgba(0,89,246,0.35)',
              },
              '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                borderColor: colors.blue,
              },
            }
          }}
        />
      </Box>

      {/* ===== Sección ESTADO ===== */}
      <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, ml: { lg: "auto" } }}>
        <Typography sx={{ color: colors.ink, fontSize: 16, fontWeight: 900, whiteSpace: "nowrap" }}>
          ESTADO
        </Typography>
        <Box sx={{ display: "flex", gap: 0.75 }}>
          <TogglePaperCompact label="Activo" value={stateCounts.ACTIVO ?? 0} status="ACTIVO" active={selectedStates.includes("ACTIVO")} onClick={() => onToggleState("ACTIVO")} />
          <TogglePaperCompact label="Venta"  value={stateCounts.VENTA ?? 0} status="VENTA" active={selectedStates.includes("VENTA")} onClick={() => onToggleState("VENTA")} />
          <TogglePaperCompact label="Baja"   value={stateCounts.BAJA ?? 0} status="BAJA" active={selectedStates.includes("BAJA")} onClick={() => onToggleState("BAJA")} />
          <TogglePaperCompact label="Futuro" value={stateCounts.FUTURO ?? 0} status="FUTURO" active={selectedStates.includes("FUTURO")} onClick={() => onToggleState("FUTURO")} />
        </Box>

        {/* Toggle de orden */}
        <ToggleButtonGroup
          exclusive
          size="small"
          value={sortOrder}
          onChange={(_, val) => { if (val) onChangeSort(val); }}
          sx={{
            '& .MuiToggleButton-root': {
              width: 36,
              height: 36,
              p: 0,
              lineHeight: 1, 
              color: colors.blue,
              borderColor: colors.line,
              backgroundColor: "rgba(255,255,255,0.88)",
              '&.Mui-selected': {
                bgcolor: colors.blue,
                color: '#fff',
                borderColor: colors.blue,
                '&:hover': { bgcolor: '#004be0', borderColor: '#004be0' },
              },
            },
          }}
        >
          <Tooltip title="Más nuevos primero" arrow>
            <ToggleButton value="desc" aria-label="orden-desc"><OrderIcon dir="desc" /></ToggleButton>
          </Tooltip>
          <Tooltip title="Más antiguos primero" arrow>
            <ToggleButton value="asc" aria-label="orden-asc"><OrderIcon dir="asc" /></ToggleButton>
          </Tooltip>
        </ToggleButtonGroup>
      </Box>
    </Box>
  );
}
